import { connect } from 'react-redux';
import { GenericObj } from '@solvhealth/types/interfaces/generics';
import PaperworkStart from './components/Start';
import PaperworkPatient from './components/Patient';
import PaperworkHealthcareContacts from './components/HealthcareContacts';
import VerificationAndReview from './components/VerificationAndReview';
import PaperworkEmployer from './components/Employer';
import PaperworkPayment from './components/Payment';
import PaperworkFinalize from './components/Finalize';
import PaperworkCustom from './components/Custom';
import PaperworkConsent from './components/Consent';
import PaperworkComplete from './components/Complete';
import ProviderGroupPaperworkPatient from './providerGroups/components/Patient';
import PaperworkMedical from './providerGroups/components/Medical';
import PaperworkLifestyleFactors from './providerGroups/components/LifestyleFactors';
import ProviderGroupPaperworkHealthcareContacts from './providerGroups/components/HealthcareContacts';
import ProviderGroupPaperworkVerificationAndReview from './providerGroups/components/VerificationAndReview';
import ProviderGroupPaperworkFinalize from './providerGroups/components/Finalize';
import { PaperworkSagas } from '../../sagas/paperwork';
import { ConsentFormSignaturesSagas } from '../../sagas/consentFormSignatures';
import { LocationConsentFormSagas } from '../../sagas/locationConsentForms';
import { setPageView, sendDataToState, toggleFinalizeEditMode } from '../../ducks/paperwork';
import { apiGetDispatchable } from '../../core/dapi';
import { getBookingById } from '../../core/dapi/newBooking';
import { bookingError, receiveBooking } from '../../actions/account';
import { locationError, receiveLocation } from '../../actions/location';
import { showModal } from '../../actions/runtime';
import { LOGIN_SIGN_UP_MODAL } from '../LoginSignUpModal';
import { getProfileFromBooking, getInsuranceProfileById } from '../../core/util/account';
import { safeGet } from '../../core/util/object';
import { setLoginAction } from '../../actions/login';
import { LOGIN_ACTION_LOGIN, LOGIN_ACTION_SIGN_UP } from '../../constants';
import { setPastBooking } from '../../actions/pastBookings';
import { noOp } from '../../actions';
import { healthDispatch } from '../../routes/addInsurance';
import withAuthentication from '~/core/login/withAuthentication';
import { getLocationUrl } from '~/core/dapi/location';

const getPreloadedData = (state: any, ownProps: any) => {
  const accountSummary = state.account.summary;
  const booking = state.pastBookings[ownProps.bookingId] || {};
  const { newBooking } = state; // if the user hasn't closed their browser session since they booked, we'll have this data available to prefill with if they don't login
  const userProfile = getProfileFromBooking(accountSummary, booking) || {};

  const insuranceProfiles = safeGet(accountSummary, [])`insurance_profiles`;
  const insuranceProfileId = userProfile.insurance_profile_id;
  const insuranceProfile = getInsuranceProfileById(insuranceProfiles, insuranceProfileId) || {};

  return {
    newBooking,
    booking,
    userProfile,
    insuranceProfile,
  };
};

const mapStateToProps = (state: any, ownProps: any) => ({
  location: state.location,
  booking: state.pastBookings[ownProps.bookingId],
  paperwork: state.paperwork,
  newBooking: state.newBooking,
  preloadedData: getPreloadedData(state, ownProps),
  isAccountSummaryLoading: state.account.accountSummaryLoading,
  loggingIn: state.login.fetching,
  insurers: state.insurers,
  insuranceCoverage: state.npi,
  addInsurance: state.addInsurance,
  insuranceCard: {
    front: {
      base64: state.imageData.front,
      scanStarted: state.addInsurance.front.scanStarted,
      scanCompleted: state.addInsurance.front.scanCompleted,
      error: state.addInsurance.front.error,
    },
    back: {
      base64: state.imageData.back,
      scanStarted: state.addInsurance.back.scanStarted,
      scanCompleted: state.addInsurance.back.scanCompleted,
      error: state.addInsurance.back.error,
    },
  },
  photoId: state.photoId,
  searchPreferences: state.searchPreferences,
});

const mapDispatchToProps = (dispatch: any, ownProps: any) => ({
  toggleFinalizeEditMode: (value: any) => dispatch(toggleFinalizeEditMode(value)),
  setPageView: (value: any) => dispatch(setPageView(value)),
  sendFormDataToState: (data: any) => dispatch(sendDataToState(data)),
  fetchBooking: (bookingId: any) =>
    dispatch(apiGetDispatchable(getBookingById(bookingId), receiveBooking, bookingError)),
  setPastBooking: (bookingId: any) =>
    dispatch(apiGetDispatchable(getBookingById(bookingId), setPastBooking, noOp)),
  fetchLocation: (locationId: any) => {
    dispatch(
      apiGetDispatchable(getLocationUrl(locationId), receiveLocation, locationError(locationId))
    );
  },

  submitPaperworkPage: (
    newForm: GenericObj,
    bookingId: string,
    paperworkStatus: string,
    onSuccess: (...args: any[]) => void,
    extraFields?: GenericObj
  ) =>
    dispatch({
      type: PaperworkSagas.SUBMIT_PAPERWORK_PAGE,
      newForm,
      bookingId,
      paperworkStatus,
      onSuccess,
      extraFields,
    }),

  submitPaperworkStatusLog: (logData: any) => {
    dispatch({
      type: PaperworkSagas.SUBMIT_PAPERWORK_STATUS_LOG,
      logData,
    });
  },

  initializePaperworkData: (bookingId: any, location: any) =>
    dispatch({
      type: PaperworkSagas.INITIALIZE_PAPERWORK_DATA,
      bookingId,
      location,
      usePublicData: !ownProps.isLoggedIn,
    }),

  fetchConsentForms: (locationId: any) =>
    dispatch({ type: LocationConsentFormSagas.FETCH_CONSENT_FORMS, locationId }),

  createConsentFormSignatures: (
    bookingId: any,
    locationConsentFormIds: any,
    signature: any,
    onSuccess: any
  ) =>
    dispatch({
      type: ConsentFormSignaturesSagas.CREATE_CONSENT_FORM_SIGNATURES,
      bookingId,
      locationConsentFormIds,
      signature,
      onSuccess,
    }),

  openLoginModal: () => {
    dispatch(setLoginAction(LOGIN_ACTION_LOGIN));
    dispatch(showModal(LOGIN_SIGN_UP_MODAL));
  },

  openSignUpModal: () => {
    dispatch(setLoginAction(LOGIN_ACTION_SIGN_UP));
    dispatch(showModal(LOGIN_SIGN_UP_MODAL));
  },

  ...healthDispatch(dispatch),
});

const ConnectedPaperworkStart = withAuthentication(
  connect(mapStateToProps, mapDispatchToProps)(PaperworkStart)
);

const ConnectedPaperworkPatient = connect(mapStateToProps, mapDispatchToProps)(PaperworkPatient);

const ConnectedPaperworkHealthcareContacts = connect(
  mapStateToProps,
  mapDispatchToProps
)(PaperworkHealthcareContacts);

const ConnectedPaperworkEmployer = connect(mapStateToProps, mapDispatchToProps)(PaperworkEmployer);

const ConnectedPaperworkPayment = connect(mapStateToProps, mapDispatchToProps)(PaperworkPayment);

const ConnectedPaperworkFinalize = connect(mapStateToProps, mapDispatchToProps)(PaperworkFinalize);

const ConnectedPaperworkCustomFields = connect(
  mapStateToProps,
  mapDispatchToProps
)(PaperworkCustom);

const ConnectedPaperworkVerificationAndReview = connect(
  mapStateToProps,
  mapDispatchToProps
)(VerificationAndReview);

const ConnectedPaperworkConsent = connect(mapStateToProps, mapDispatchToProps)(PaperworkConsent);

const ConnectedPaperworkComplete = connect(mapStateToProps, mapDispatchToProps)(PaperworkComplete);

const ConnectedProviderGroupPaperworkPatient = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProviderGroupPaperworkPatient);

const ConnectedPaperworkMedical = connect(mapStateToProps, mapDispatchToProps)(PaperworkMedical);

const ConnectedPaperworkLifestyleFactors = connect(
  mapStateToProps,
  mapDispatchToProps
)(PaperworkLifestyleFactors);

const ConnectedProviderGroupPaperworkHealthcareContacts = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProviderGroupPaperworkHealthcareContacts);

const ConnectedProviderGroupPaperworkVerificationAndReview = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProviderGroupPaperworkVerificationAndReview);

const ConnectedProviderGroupPaperworkFinalize = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProviderGroupPaperworkFinalize);

export {
  ConnectedPaperworkStart,
  ConnectedPaperworkPatient,
  ConnectedPaperworkHealthcareContacts,
  ConnectedPaperworkEmployer,
  ConnectedPaperworkPayment,
  ConnectedPaperworkCustomFields,
  ConnectedPaperworkFinalize,
  ConnectedPaperworkVerificationAndReview,
  ConnectedPaperworkConsent,
  ConnectedPaperworkComplete,
  ConnectedProviderGroupPaperworkPatient,
  ConnectedProviderGroupPaperworkHealthcareContacts,
  ConnectedPaperworkLifestyleFactors,
  ConnectedPaperworkMedical,
  ConnectedProviderGroupPaperworkVerificationAndReview,
  ConnectedProviderGroupPaperworkFinalize,
};
